import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Users, Gift, ShoppingBag, Lock, Search, Ban, Trash2, TrendingUp, Mail, CheckCircle2, Send } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang, dateLocale } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AdminExploreProducts } from "@/components/AdminExploreProducts";

export default function Admin() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState({ messages: [], new_count: 0 });
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, u, c] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/contact-messages"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setContacts(c.data);
    } catch (err) {
      console.error("admin load failed", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const search = async () => {
    try {
      const res = await api.get("/admin/users", { params: { q: query } });
      setUsers(res.data);
    } catch (err) {
      console.error("admin user search failed", err);
    }
  };

  const toggleBlock = async (uid) => {
    try {
      const res = await api.put(`/admin/users/${uid}/block`);
      toast.success(res.data.is_blocked ? t("user_blocked") : t("user_unblocked"));
      search();
    } catch { toast.error(t("error_generic")); }
  };

  const removeUser = async (uid) => {
    if (!window.confirm(t("user_del_confirm"))) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success(t("user_deleted"));
      load();
    } catch { toast.error(t("error_generic")); }
  };

  const toggleContact = async (cid) => {
    try {
      await api.put(`/admin/contact-messages/${cid}/status`);
      const res = await api.get("/admin/contact-messages");
      setContacts(res.data);
    } catch (err) {
      console.error("contact toggle failed", err);
      toast.error(t("error_generic"));
    }
  };

  const runDigest = async () => {
    try {
      const res = await api.post("/admin/run-weekly-digest");
      toast.success(t("digest_sent", { n: res.data.sent }));
    } catch (err) {
      console.error("digest failed", err);
      toast.error(t("error_generic"));
    }
  };

  if (user?.role !== "admin") return <div className="text-center py-20 text-muted-foreground">{t("no_access")}</div>;
  if (!stats) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const cards = [
    { label: t("stat_users"), value: stats.total_users, icon: Users },
    { label: t("stat_new7"), value: stats.new_users_week, icon: TrendingUp },
    { label: t("stat_sessions"), value: stats.active_sessions, icon: Users },
    { label: t("stat_wl"), value: stats.total_wishlists, icon: Gift },
    { label: t("stat_items"), value: stats.total_items, icon: ShoppingBag },
    { label: t("stat_res"), value: stats.reservations, icon: Lock },
    { label: t("stat_pur"), value: stats.purchases, icon: ShoppingBag },
  ];

  return (
    <div data-testid="admin-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-6">{t("admin_panel")}</h1>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {cards.map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-card rounded-2xl border border-border/50 p-5" data-testid={`admin-stat-${label}`}>
            <Icon size={18} className="text-primary mb-2" />
            <p className="font-heading text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-card rounded-2xl border border-border/50 p-6">
          <h2 className="font-heading font-semibold mb-4">{t("top_clicked")}</h2>
          <div className="space-y-3">
            {stats.top_clicked_items.map((it) => (
              <div key={`${it.name}-${it.store}`} className="flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <p className="font-medium truncate">{it.name}</p>
                  <p className="text-xs text-muted-foreground">{it.store} · {formatPrice(it.price)}</p>
                </div>
                <Badge variant="secondary" className="rounded-full shrink-0">{it.click_count} {t("clicks_n")}</Badge>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-card rounded-2xl border border-border/50 p-6">
          <h2 className="font-heading font-semibold mb-4">{t("top_stores")}</h2>
          <div className="space-y-3">
            {stats.top_stores.map((s) => (
              <div key={s.store} className="flex items-center justify-between text-sm">
                <p className="font-medium">{s.store}</p>
                <Badge variant="secondary" className="rounded-full">{s.count} {t("items_count")}</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AdminExploreProducts />

      <div className="bg-card rounded-2xl border border-border/50 p-6 mb-8" data-testid="admin-contact-inbox">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-heading font-semibold flex items-center gap-2">
            <Mail size={17} className="text-primary" /> {t("contact_inbox")}
            {contacts.new_count > 0 && (
              <Badge className="rounded-full bg-primary text-white border-0" data-testid="admin-contact-new-badge">{contacts.new_count} {t("new_n")}</Badge>
            )}
          </h2>
          <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={runDigest} data-testid="admin-run-digest-btn">
            <Send size={12} className="mr-1" /> {t("run_digest")}
          </Button>
        </div>
        {contacts.messages.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t("no_messages")}</p>
        ) : (
          <div className="space-y-3">
            {contacts.messages.map((m) => (
              <div key={m.contact_id} className={`p-4 rounded-xl border ${m.status === "new" ? "border-primary/30 bg-primary/5" : "border-border/50"}`} data-testid={`admin-contact-row-${m.contact_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{m.name} <span className="text-muted-foreground font-normal">· {m.email}</span></p>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{m.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-1.5">{new Date(m.created_at).toLocaleString(dateLocale(lang))}</p>
                  </div>
                  <Button size="sm" variant={m.status === "new" ? "default" : "outline"} className="rounded-full h-8 text-xs shrink-0"
                    onClick={() => toggleContact(m.contact_id)} data-testid={`admin-contact-toggle-${m.contact_id}`}>
                    <CheckCircle2 size={12} className="mr-1" /> {m.status === "new" ? t("mark_replied") : t("replied_check")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-border/50 p-6">
        <h2 className="font-heading font-semibold mb-4">{t("user_mgmt")}</h2>
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder={t("admin_search_ph")} className="rounded-full pl-10" data-testid="admin-user-search" />
          </div>
          <Button onClick={search} className="rounded-full" data-testid="admin-search-btn">{t("search")}</Button>
        </div>
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.user_id} className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-secondary/50 transition-colors" data-testid={`admin-user-row-${u.username}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{u.name} <span className="text-muted-foreground">@{u.username}</span>
                  {u.role === "admin" && <Badge className="ml-2 rounded-full text-[10px]">admin</Badge>}
                  {u.is_blocked && <Badge variant="destructive" className="ml-2 rounded-full text-[10px]">{t("blocked")}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              </div>
              {u.role !== "admin" && (
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={() => toggleBlock(u.user_id)} data-testid={`admin-block-${u.username}`}>
                    <Ban size={12} className="mr-1" /> {u.is_blocked ? t("unblock") : t("block")}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full h-8 text-xs text-destructive" onClick={() => removeUser(u.user_id)} data-testid={`admin-delete-${u.username}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
